import {
  Box,
  Icon,
  IconButton,
  OsStack,
  Typography,
} from "@outschool/backpack";
import { faArrowLeft, faCalendar } from "@outschool/icons";
import { useLocalStorageState } from "@outschool/local-storage";
import { useTranslation } from "@outschool/localization";
import { dayjs, pastDurationStringWithTimes } from "@outschool/time";
import { ErrorMessage } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import React, { useMemo } from "react";

import { useMuxClassRecordingQuery } from "../../../hooks/useMuxClassroomRecording";
import PlayerOrProcessingMessage from "../../PlayerOrProcessingMessage";
import TermsAcceptance from "./TermsAcceptance";

const TERMS_ACCEPTED_AT_KEY = "view_class_recording_terms_accepted_at";
const RECORDING_DURATION_LIMIT_MINUTES = 60 * 3; // 180 minutes

interface ClassRecordingContentProps
  extends Omit<
    ReturnType<typeof useMuxClassRecordingQuery>,
    "usersCanPost" | "meeting"
  > {
  recordingUid: string;
  sectionUid: string;
  userTimeZone: string;
  handleBackClick: () => void;
  track: (
    event: string,
    properties?: {
      [propName: string]: any;
    },
  ) => void;
  handlePlayClick?: () => void;
}

export default function ClassRecordingContent({
  recordingUid,
  sectionUid,
  section,
  activity,
  startTime,
  endTime,
  canManage,
  loading,
  error,
  isExpired,
  userTimeZone,
  handleBackClick,
  handlePlayClick,
  track,
}: ClassRecordingContentProps) {
  // This component is used in learner app (RecordingPage.tsx) and website (MuxRecordingPage.tsx)
  // The learner app does not support translations, but website does
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\classRecording\\ClassRecordingContent",
  );

  const [termsAcceptedAt, setTermsAcceptedAt] = useLocalStorageState<
    string | null
  >(TERMS_ACCEPTED_AT_KEY, null);
  const termsAcceptedInLast24Hours = React.useMemo(
    () =>
      termsAcceptedAt !== null &&
      dayjs(termsAcceptedAt).isAfter(dayjs().subtract(24, "hours")),
    [termsAcceptedAt],
  );

  const handleTermsAccepted = () => {
    if (termsAcceptedInLast24Hours) {
      return;
    }
    track("accept_recording_terms", {
      activityUid: activity?.uid,
      sectionUid: section?.uid,
      recordingId: recordingUid,
    });
    setTermsAcceptedAt(new Date().toISOString());
  };

  const hasExceededRecordingDurationLimit = useMemo(() => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const duration = dayjs.duration(end.diff(start)).asMinutes();
    return duration > RECORDING_DURATION_LIMIT_MINUTES;
  }, [startTime, endTime]);

  if (loading) {
    return <Loading />;
  }

  if (error || isExpired) {
    const errorMessage =
      error?.message ||
      t`This recording is no longer available.  Outschool retains recordings for three months.`;
    return <ErrorMessage value={errorMessage} />;
  }

  if (!section) {
    OsPlatform.captureError("Recording page loaded but section is not defined");
    return (
      <ErrorMessage value="Oops, we encountered an error when loading this recording." />
    );
  }

  if (termsAcceptedInLast24Hours) {
    return (
      <OsStack direction="column" spacing={6}>
        <OsStack justifyContent="center">
          <Box
            sx={{
              width: "100%",
              overflow: "auto",
            }}
          >
            {hasExceededRecordingDurationLimit ? (
              <ErrorMessage
                value={t(
                  "Sorry, this recording is over {{numMinutes}} minutes and cannot be displayed.",
                  { numMinutes: RECORDING_DURATION_LIMIT_MINUTES },
                )}
              />
            ) : (
              <PlayerOrProcessingMessage
                sectionUid={sectionUid}
                recordingUid={recordingUid}
                handlePlayClick={handlePlayClick}
              />
            )}
          </Box>
        </OsStack>
        <OsStack direction="row" spacing={2}>
          <IconButton
            icon={faArrowLeft}
            variant="text"
            color="neutral"
            onClick={handleBackClick}
            sx={{ width: "40px", height: "40px" }}
          />
          <OsStack direction="column" spacing={2}>
            <Typography variant="h3">{activity?.title}</Typography>
            <Typography
              variant="body1"
              color="neutral.500"
              sx={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <Icon icon={faCalendar} size="small" />
              {startTime &&
                endTime &&
                pastDurationStringWithTimes(startTime, endTime, userTimeZone)}
            </Typography>
          </OsStack>
        </OsStack>
      </OsStack>
    );
  } else {
    return (
      <TermsAcceptance
        handleTermsAccepted={handleTermsAccepted}
        canManage={canManage}
      />
    );
  }
}
